// API service for backend integration
// This file contains the API calls for the three main services:
// 1. Speech to Text
// 2. Machine Translation
// 3. Text to Speech

// API Base URLs from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const STT_BASE_URL = process.env.REACT_APP_STT_BASE_URL || '';
const TTS_BASE_URL = process.env.REACT_APP_TTS_BASE_URL || '';

export interface SpeechToTextResponse {
  text: string;
  confidence: number;
  language: string;
}

export interface TranslationResponse {
  translatedText: string;
  sourceLanguage: 'ja';
  targetLanguage: 'en';
  confidence: number;
}

export interface TextToSpeechResponse {
  audioUrl: string;
  audioData: Blob; // Thêm audioData để lưu trữ blob
  duration: number;
}

export interface TranslationHistory {
  id: string;
  sourceText: string;
  translatedText: string;
  sourceLanguage: 'ja';
  targetLanguage: 'en';
  timestamp: string;
  audioUrl?: string;
  audioData?: Blob; // Thêm audioData để lưu trữ blob
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  private async requestWithBase<T>(
    baseUrl: string,
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${baseUrl}${endpoint}`;
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('API Request (custom base) failed:', error);
      throw error;
    }
  }

  // Speech to Text API
  async speechToText(
    audioFile: File,
    language: string = 'auto'
  ): Promise<SpeechToTextResponse> {
    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('language', language);

    try {
      console.log('[DEBUG] STT request payload', {
        file: {
          name: audioFile.name,
          type: audioFile.type,
          sizeBytes: audioFile.size,
        },
        language,
        url: `${STT_BASE_URL}/api/speech-to-text`,
      });
      const response = await fetch(`${STT_BASE_URL}/api/speech-to-text`, {
        method: 'POST',
        // Important: Do not set Content-Type for FormData; browser will set boundary
        headers: {
          'ngrok-skip-browser-warning': 'true',
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Speech to Text API Error: ${response.status}`);
      }

      const json = await response.json();
      console.log('[DEBUG] STT response', json);
      return json;
    } catch (error) {
      console.error('Speech to Text failed:', error);
      // Return mock data for development
      return {
        text: 'Hello, nice to meet you',
        confidence: 0.95,
        language: 'en',
      };
    }
  }

  // Machine Translation API - Japanese to English only
  async translateText(text: string): Promise<TranslationResponse> {
    try {
      const response = await this.request<any>('/api/v1/translate', {
        method: 'POST',
        body: JSON.stringify({ text }),
      });

      // Map response to our type
      const mapped: TranslationResponse = {
        translatedText: response.translation,
        sourceLanguage: 'ja',
        targetLanguage: 'en',
        confidence: 1.0,
      };

      return mapped;
    } catch (error) {
      console.error('Translation failed:', error);
      // Return mock data for development
      return {
        translatedText: 'Translation will appear here...',
        sourceLanguage: 'ja',
        targetLanguage: 'en',
        confidence: 0.92,
      };
    }
  }

  // Text to Speech API - English only
  async textToSpeech(text: string): Promise<TextToSpeechResponse> {
    try {
      console.log('[DEBUG] TTS request payload', {
        text,
        language: 'english',
        url: `${TTS_BASE_URL}/audio/english`,
      });

      const response = await fetch(`${TTS_BASE_URL}/audio/english`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ text }),
      });
      console.log('🚀 ~ ApiService ~ textToSpeech ~ response:', response);

      if (!response.ok) {
        throw new Error(
          `TTS Error: ${response.status} - ${response.statusText}`
        );
      }

      // const data = await response.json();
      // console.log('[DEBUG] TTS response success:', data);
      if (!response.body) {
        throw new Error('TTS response body is empty');
      }

      // Convert base64 to blob
      // const audioBlob = this.base64ToBlob(response.body., data.mime_type);
      const audioBlob = await response.blob();

      const audioUrl = URL.createObjectURL(audioBlob);

      return {
        audioUrl,
        audioData: audioBlob,
        duration: 0, // Will be calculated when playing
      };
    } catch (error) {
      console.error('Text to Speech failed:', error);

      // Return mock data for development
      return {
        audioUrl: '/mock-audio.mp3',
        audioData: new Blob(),
        duration: 5.0,
      };
    }
  }

  // Helper function to convert base64 to blob
  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  // Test TTS server connection
  async testTTSConnection(): Promise<boolean> {
    try {
      if (!TTS_BASE_URL) {
        console.error('[DEBUG] TTS_BASE_URL is not configured');
        return false;
      }
      const response = await fetch(`${TTS_BASE_URL}/health`, {
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
      });

      console.log('[DEBUG] TTS server connection test:', {
        status: response.status,
        data: await response.json(),
      });

      return response.ok;
    } catch (error) {
      console.error('[DEBUG] TTS server connection test failed:', error);
      return false;
    }
  }

  // Test TTS request format
  async testTTSRequest(): Promise<{ success: boolean; error?: string }> {
    try {
      if (!TTS_BASE_URL) {
        return {
          success: false,
          error: 'TTS_BASE_URL is not configured in environment variables',
        };
      }
      const testPayload = { text: 'Hello' };
      console.log('[DEBUG] Testing TTS request format with:', testPayload);

      const response = await fetch(`${TTS_BASE_URL}/audio/english`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify(testPayload),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[DEBUG] TTS test request successful:', {
          status: response.status,
          message: data.message,
          hasAudioData: !!data.audio_data,
        });
        return { success: true };
      } else {
        const errorText = await response.text();
        console.error('[DEBUG] TTS test request failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
        });
        return {
          success: false,
          error: `Status ${response.status}: ${errorText}`,
        };
      }
    } catch (error) {
      console.error('[DEBUG] TTS test request error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Get translation history
  async getTranslationHistory(): Promise<TranslationHistory[]> {
    try {
      return await this.request<TranslationHistory[]>('/history');
    } catch (error) {
      console.error('Failed to fetch history:', error);
      // Return mock data for development
      return [
        {
          id: '1',
          sourceText: 'こんにちは、初めまして',
          translatedText: 'Hello, nice to meet you',
          sourceLanguage: 'ja',
          targetLanguage: 'en',
          timestamp: new Date().toISOString(),
          audioData: new Blob(),
        },
        {
          id: '2',
          sourceText: '電車が遅延しております',
          translatedText: 'The train is delayed',
          sourceLanguage: 'ja',
          targetLanguage: 'en',
          timestamp: new Date().toISOString(),
          audioData: new Blob(),
        },
        {
          id: '3',
          sourceText: 'お疲れ様です',
          translatedText: 'Good work',
          sourceLanguage: 'ja',
          targetLanguage: 'en',
          timestamp: new Date().toISOString(),
          audioData: new Blob(),
        },
      ];
    }
  }

  // Save translation to history
  async saveTranslation(
    translation: Omit<TranslationHistory, 'id' | 'timestamp'>
  ): Promise<TranslationHistory> {
    try {
      return await this.request<TranslationHistory>('/history', {
        method: 'POST',
        body: JSON.stringify(translation),
      });
    } catch (error) {
      console.error('Failed to save translation:', error);
      // Return mock data for development
      return {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        ...translation,
        audioData: translation.audioData || new Blob(),
      };
    }
  }
}

export const apiService = new ApiService();
